/**
 * 全站统一分页：默认带「每页条数」+「跳转」，并尽量联动列表刷新。
 * 创建于 2026-08-24
 * 最后更新：2026-09-06
 * - 未写 layout 时用统一 layout（含 sizes）
 * - 业务已写 layout 时：不再强行插入 sizes（写死 page-size=10 且未接请求的页面会假切换）
 * - 显式 page-size（含写死 10）一律尊重，不再被 localStorage 全局覆盖显示
 * - 弹窗 / 抽屉内：改条数不回写外层列表、不自动刷新外层
 * - getUnifiedPageSize / setUnifiedPageSize 仅供业务页显式选用
 */
import Vue from 'vue'

export const UNIFIED_PAGE_SIZES = [10, 20, 50, 100]
export const UNIFIED_LAYOUT = 'total, sizes, prev, pager, next, jumper'
const STORAGE_KEY = 'sp_list_page_size'

/** 读取用户上次选择的每页条数（仅显式调用时使用） */
export function getUnifiedPageSize() {
  const n = parseInt(window.localStorage.getItem(STORAGE_KEY) || '10', 10)
  return UNIFIED_PAGE_SIZES.includes(n) ? n : 10
}

/** 记住每页条数（仅显式调用时使用） */
export function setUnifiedPageSize(size) {
  const n = Number(size)
  if (!UNIFIED_PAGE_SIZES.includes(n)) return
  window.localStorage.setItem(STORAGE_KEY, String(n))
  Vue.prototype.$listPageSize = n
}

/**
 * 规范化 layout。
 * - 未传 / 非法：用统一 layout
 * - 已传：只补 jumper，不擅自插入 sizes（避免「能改条数但接口仍 10」）
 * 最后更新：2026-09-06
 */
export function normalizePaginationLayout(layout) {
  if (!layout || typeof layout !== 'string') return UNIFIED_LAYOUT
  const parts = layout.split(',').map((s) => s.trim()).filter(Boolean)
  if (!parts.includes('pager')) return layout

  if (!parts.includes('jumper')) {
    parts.push('jumper')
  }
  return parts.join(', ')
}

/** 是否位于弹窗/抽屉内（含 append-to-body）。创建于 2026-09-05 */
function isInsideOverlay(vm) {
  let p = vm && vm.$parent
  while (p) {
    const name = (p.$options && (p.$options.name || p.$options._componentTag)) || ''
    if (name === 'ElDialog' || name === 'ElDrawer') return true
    p = p.$parent
  }
  try {
    const el = vm && vm.$el
    if (el && el.nodeType === 1 && typeof el.closest === 'function') {
      return !!(el.closest('.el-dialog') || el.closest('.el-drawer'))
    }
  } catch (e) {
    /* ignore */
  }
  return false
}

function findListHost(paginationVm) {
  let p = paginationVm.$parent
  while (p) {
    const name = (p.$options && (p.$options.name || p.$options._componentTag)) || ''
    // 不跨过弹窗去找外层列表宿主
    if (name === 'ElDialog' || name === 'ElDrawer') return null
    if (
      typeof p.handleCurrentChange === 'function' ||
      typeof p.handleQuery === 'function' ||
      typeof p.loadList === 'function' ||
      typeof p.getList === 'function' ||
      typeof p.fetchList === 'function' ||
      typeof p.handleSearch === 'function'
    ) {
      return p
    }
    p = p.$parent
  }
  return paginationVm.$parent
}

function syncHostPageSize(paginationVm, size) {
  let p = paginationVm.$parent
  while (p) {
    const name = (p.$options && (p.$options.name || p.$options._componentTag)) || ''
    if (name === 'ElDialog' || name === 'ElDrawer') return null

    const data = p.$data || {}
    let hit = false
    if (Object.prototype.hasOwnProperty.call(data, 'pageSize')) {
      p.pageSize = size
      hit = true
    }
    if (Object.prototype.hasOwnProperty.call(data, 'PageSize')) {
      p.PageSize = size
      hit = true
    }
    if (Object.prototype.hasOwnProperty.call(data, 'intPageSize')) {
      p.intPageSize = size
      hit = true
    }
    // 弹窗内自有条数字段（如 historyPageSize），不回写外层列表 pageSize
    if (Object.prototype.hasOwnProperty.call(data, 'historyPageSize')) {
      return p
    }
    if (hit) return p
    p = p.$parent
  }
  const host = findListHost(paginationVm)
  if (host) {
    Vue.set(host, 'pageSize', size)
  }
  return host
}

function reloadListHost(paginationVm) {
  const host = findListHost(paginationVm)
  if (!host) return

  if (typeof host.handleCurrentChange === 'function') {
    host.handleCurrentChange(1)
    return
  }
  if (host.page !== undefined) host.page = 1
  if (host.pageIndex !== undefined) host.pageIndex = 1
  if (host.currentPage !== undefined) host.currentPage = 1

  if (typeof host.loadList === 'function') {
    host.loadList(1)
    return
  }
  if (typeof host.handleQuery === 'function') {
    host.handleQuery()
    return
  }
  if (typeof host.getList === 'function') {
    host.getList()
    return
  }
  if (typeof host.fetchList === 'function') {
    host.fetchList()
    return
  }
  if (typeof host.handleSearch === 'function') {
    host.handleSearch()
  }
}

/**
 * 注册统一分页组件（覆盖 Element 默认 ElPagination）
 * @param {typeof Vue} VueCtor
 * @param {*} ElementUI
 */
export function installUnifiedPagination(VueCtor, ElementUI) {
  const RawPagination = ElementUI.Pagination
  if (!RawPagination) return

  VueCtor.prototype.$listPageSize = getUnifiedPageSize()

  if (RawPagination.props) {
    if (RawPagination.props.layout) {
      RawPagination.props.layout.default = UNIFIED_LAYOUT
    }
    if (RawPagination.props.pageSizes) {
      RawPagination.props.pageSizes.default = () => UNIFIED_PAGE_SIZES.slice()
    }
    if (RawPagination.props.background) {
      RawPagination.props.background.default = true
    }
  }

  VueCtor.component('ElPagination', {
    name: 'ElPagination',
    props: Object.assign({}, RawPagination.props, {
      /**
       * 前端本地切片分页时设为 true，不自动回写宿主 / 刷新列表。
       * 弹窗内会自动隔离，一般无需再传。创建于 2026-09-03
       */
      localPaging: {
        type: Boolean,
        default: false
      }
    }),
    data() {
      return {
        overridePageSize: null,
        /** 是否在弹窗/抽屉内 */
        isolatedByOverlay: false
      }
    },
    computed: {
      /** 隔离分页：显式 localPaging，或位于弹窗/抽屉内。最后更新：2026-09-05 */
      isIsolatedPaging() {
        return !!(this.localPaging || this.isolatedByOverlay)
      },
      mergedLayout() {
        return normalizePaginationLayout(this.layout)
      },
      mergedPageSizes() {
        if (Array.isArray(this.pageSizes) && this.pageSizes.length) {
          return this.pageSizes
        }
        return UNIFIED_PAGE_SIZES
      },
      /**
       * 始终尊重显式 page-size（含 10），不再用 localStorage 覆盖。
       * 最后更新：2026-09-05
       */
      mergedPageSize() {
        if (this.overridePageSize != null) return this.overridePageSize
        if (typeof this.pageSize === 'number' && this.pageSize > 0) return this.pageSize
        return 10
      },
      mergedBackground() {
        return this.background !== false
      }
    },
    created() {
      this.isolatedByOverlay = isInsideOverlay(this)
    },
    mounted() {
      if (!this.isolatedByOverlay) {
        this.isolatedByOverlay = isInsideOverlay(this)
      }
    },
    render(h) {
      const listeners = Object.assign({}, this.$listeners)
      const prevSizeChange = listeners['size-change']
      const isolated = this.isIsolatedPaging

      listeners['size-change'] = (size) => {
        this.overridePageSize = size
        // 仅回写当前页宿主；不再写入全站 localStorage
        if (!isolated) {
          syncHostPageSize(this, size)
        }

        if (typeof prevSizeChange === 'function') {
          prevSizeChange(size)
        } else if (Array.isArray(prevSizeChange)) {
          prevSizeChange.forEach((fn) => {
            if (typeof fn === 'function') fn(size)
          })
        }

        // 业务页已自行处理 size-change 时，不再自动刷新，避免打两次接口
        if (!prevSizeChange && !isolated) {
          this.$nextTick(() => reloadListHost(this))
        }
      }

      return h(RawPagination, {
        props: Object.assign({}, this.$props, {
          layout: isolated
            ? (this.layout || 'total, sizes, prev, pager, next, jumper')
            : this.mergedLayout,
          pageSize: this.mergedPageSize,
          pageSizes: this.mergedPageSizes,
          background: this.mergedBackground
        }),
        on: listeners,
        attrs: this.$attrs
      })
    }
  })
}

/**
 * 列表请求若只带 page、未带每页条数，则补默认 10（不再读 localStorage）。
 * @param {object} config axios config
 */
export function injectUnifiedPageSizeToRequest(config) {
  if (!config || !config.params || typeof config.params !== 'object') return config

  const params = config.params
  const page = params.page
  if (page === undefined || page === null || page === '') return config

  const hasSize =
    params.intPageSize != null ||
    params.pageSize != null ||
    params.PageSize != null ||
    params.page_size != null
  if (hasSize) return config

  params.intPageSize = 10
  params.pageSize = 10
  return config
}
